// Studby — app shell with top bar + mobile bottom navigation.
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Camera, FileText, MessageCircle, LogOut, Sprout } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/app/solve", label: "Solve", icon: Camera, id: "nav-solve" },
  { to: "/app/quiz", label: "Quiz", icon: FileText, id: "nav-quiz" },
  { to: "/app/tutor", label: "Tutor", icon: MessageCircle, id: "nav-tutor" },
];

export function AppShell({ children }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    // Navigate to the public landing page FIRST, then clear auth — otherwise
    // ProtectedRoute re-renders and bounces the user to /login.
    navigate("/");
    logout();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-card/90 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <NavLink to="/app/dashboard" className="flex items-center gap-2" data-testid="app-logo">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-primary text-primary-foreground">
              <Sprout size={18} aria-hidden="true" />
            </span>
            <span className="font-heading font-extrabold text-lg text-primary">Studby</span>
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.id}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-xs text-muted-foreground truncate max-w-[140px]">
              {user?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-muted-foreground"
            >
              <LogOut size={16} aria-hidden="true" />
            </Button>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-6 pb-24 sm:pb-8">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-card border-t border-border">
        <div className="grid grid-cols-4">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`${n.id}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 py-2.5 text-[11px] transition-colors ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`
                }
              >
                <Icon size={20} aria-hidden="true" />
                {n.label}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
